import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { paymentsAPI } from '@/utils/api';
import { Zap, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/products');
    }
  }, [sessionId, attempts]);

  const checkPaymentStatus = async () => {
    try {
      const response = await paymentsAPI.getCheckoutStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Clear cart
        localStorage.setItem('cart', JSON.stringify([]));
      } else if (attempts < maxAttempts) {
        setTimeout(() => setAttempts(attempts + 1), 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      toast.error('Failed to verify payment');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10] border border-white/10 rounded-2xl p-12">
          <div className="flex items-center justify-center mb-8">
            <Zap className="h-10 w-10 text-yellow-400" />
            <span className="text-3xl font-bold ml-2 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              LuxLyx
            </span>
          </div>

          {status === 'checking' && (
            <>
              <Clock className="h-20 w-20 text-yellow-400 mx-auto mb-6 animate-spin" />
              <h2 className="text-3xl font-bold mb-4">Processing Payment...</h2>
              <p className="text-gray-400 mb-8">Please wait while we confirm your payment</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4">Order Confirmed!</h2>
              <p className="text-gray-400 mb-8">
                Thank you for your purchase. Your order has been confirmed and will be shipped soon.
              </p>
              <div className="flex flex-col space-y-3">
                <Button
                  data-testid="view-orders-btn"
                  onClick={() => navigate('/orders')}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold"
                >
                  View Orders
                </Button>
                <Button
                  data-testid="continue-shopping-btn"
                  variant="outline"
                  onClick={() => navigate('/products')}
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  Continue Shopping
                </Button>
              </div>
            </>
          )}

          {(status === 'timeout' || status === 'error') && (
            <>
              <div className="h-20 w-20 rounded-full bg-yellow-400/10 border-2 border-yellow-400 flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-yellow-400" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Payment Processing</h2>
              <p className="text-gray-400 mb-8">
                Your payment is being processed. Please check your orders page or email for confirmation.
              </p>
              <Button
                data-testid="view-orders-btn"
                onClick={() => navigate('/orders')}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold"
              >
                View Orders
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
